import { z } from "zod";
/** Operational queries use the selected store or all stores authorized for the caller. */
export const analyticsScopeQuery = z.object({ storeId: z.string().uuid().optional() });
export const analyticsIssuesBreakdown = z.object({
    total: z.number().int(),
    open: z.number().int(),
    in_progress: z.number().int(),
    resolved: z.number().int(),
    assigned: z.number().int(),
    rejected: z.number().int(),
    actionable: z.number().int(),
    // Critical issues still requiring action, excluding resolved/terminal rows.
    critical: z.number().int(),
});
export const analyticsTasksBreakdown = z.object({
    total: z.number().int(),
    open: z.number().int(),
    in_progress: z.number().int(),
    assigned: z.number().int(),
    rejected: z.number().int(),
    pending: z.number().int(),
    submitted: z.number().int(),
    verified: z.number().int(),
    completed: z.number().int(),
});
export const analyticsSessionsBreakdown = z.object({
    total_last_24h: z.number().int(),
    total_last_7d: z.number().int(),
    by_source: z.record(z.number().int()),
});
export const analyticsProductsBreakdown = z.object({
    total: z.number().int(),
    with_image: z.number().int(),
    without_image: z.number().int(),
});
export const analyticsSummary = z.object({
    issues: analyticsIssuesBreakdown,
    tasks: analyticsTasksBreakdown,
    sessions: analyticsSessionsBreakdown,
    // Company catalog diagnostics are suppressed for store-scoped requests.
    products: analyticsProductsBreakdown.nullable(),
});
export const analyticsIssuesResponse = z.object({
    actionableTotal: z.number().int(),
    actionableByType: z.record(z.number().int()),
    actionableBySeverity: z.record(z.number().int()),
    total: z.number().int(),
    byStatus: z.record(z.number().int()),
    bySeverity: z.record(z.number().int()),
    byType: z.record(z.number().int()),
});
export const analyticsTasksResponse = z.object({
    pending: z.number().int(),
    total: z.number().int(),
    byStatus: z.record(z.number().int()),
    byType: z.record(z.number().int()),
});
export const analyticsSessionsResponse = z.object({
    total: z.number().int(),
    byStatus: z.record(z.number().int()),
    bySource: z.record(z.number().int()),
    byDayOfWeek: z.record(z.number().int()),
});
export const analyticsProductsResponse = z.object({
    scope: z.literal("tenant_catalog"),
    total: z.number().int(),
    withImage: z.number().int(),
    withoutImage: z.number().int(),
    byCategory: z.array(z.object({
        categoryId: z.string().uuid().nullable(),
        categoryName: z.string().nullable(),
        count: z.number().int(),
    })),
});
// ── Per-task capture analytics (Task Analytics dashboard) ────────────────────
export const taskAnalyticsSummary = z.object({
    skusDetected: z.number().int(),
    totalFacings: z.number().int(),
    captures: z.number().int(),
    // Legacy name: gap-region count / (product facings + gap-region count).
    // This detection-count proxy is not SKU availability or physical shelf space.
    // Null when there is nothing measured yet.
    avgOos: z.number().nullable(),
});
export const brandShareEntry = z.object({
    brand: z.string(),
    // Explicit identity distinguishes a catalogue brand from the unlinked bucket.
    isAttributed: z.boolean().optional(),
    facings: z.number().int(),
    sharePct: z.number(),
});
export const categoryShareEntry = z.object({
    categoryId: z.string().uuid().nullable(),
    categoryName: z.string(),
    facings: z.number().int(),
    sharePct: z.number(),
});
export const shelfBrandBreakdown = z.object({
    // section_index of the vision-detected shelf, or null for unassigned.
    shelfIndex: z.number().int().nullable(),
    shelfLabel: z.string(),
    totalFacings: z.number().int(),
    brands: z.array(z.object({ brand: z.string(), isAttributed: z.boolean().optional(), facings: z.number().int() })),
});
/**
 * Recognition-pipeline diagnostics — Phase 1 of the analytics gap plan
 * (`docs/superpowers/analytics-gaps.md`, §8). Surfaced beside every SOS
 * chart so operators can see when the underlying read is unreliable.
 *
 * Denominator for every percentage here is "product-type detections in
 * scope" (we exclude empty_space / shelf_gap / price_tag rows because they
 * don't carry decision/view metadata and would skew the ratio). Percentages
 * are 0..100; means are 0..1. Counts that involve zero-divisor filters are
 * nulled rather than zeroed — a chart caption needs to know "no signal"
 * vs "signal = 0".
 */
export const recognitionDiagnostics = z.object({
    totalDetections: z.number().int(),
    inDbCount: z.number().int(),
    uncertainCount: z.number().int(),
    notInDbCount: z.number().int(),
    unknownCount: z.number().int(),
    doubtCount: z.number().int(),
    backFacingCount: z.number().int(),
    lowConfidenceCount: z.number().int(),
    recognitionCoveragePct: z.number().nullable(),
    unknownFacingsPct: z.number().nullable(),
    doubtRatePct: z.number().nullable(),
    frontalVisibilityPct: z.number().nullable(),
    meanConfidence: z.number().nullable(),
    meanSimilarityInDb: z.number().nullable(),
    /**
     * Composite capture-quality score (spec §6):
     *   0.30 × (MeanConf × 100) + 0.30 × Coverage% + 0.20 × (100 − Doubt%) + 0.20 × (100 − Unknown%)
     * Null if any required component is null.
     */
    qualityScore: z.number().nullable(),
});
export const taskAnalyticsResponse = z.object({
    taskId: z.string().uuid(),
    summary: taskAnalyticsSummary,
    diagnostics: recognitionDiagnostics,
    brandShare: z.array(brandShareEntry),
    categoryDistribution: z.array(categoryShareEntry),
    brandsPerShelf: z.array(shelfBrandBreakdown),
});
/**
 * Session-level aggregate of the same shape as the task report — folds in
 * every capture across every task in the session. Used by the new
 * Analytics tab on the scan-detail page.
 */
export const sessionCoverage = z.object({
    fixturesAssigned: z.number().int(),
    fixturesScanned: z.number().int(),
    tasksTotal: z.number().int(),
    tasksCompleted: z.number().int(),
    tasksPending: z.number().int(),
    tasksIncomplete: z.number().int(),
});
export const sessionAnalyticsResponse = z.object({
    sessionId: z.string().uuid(),
    taskCount: z.number().int(),
    summary: taskAnalyticsSummary,
    diagnostics: recognitionDiagnostics,
    brandShare: z.array(brandShareEntry),
    categoryDistribution: z.array(categoryShareEntry),
    brandsPerShelf: z.array(shelfBrandBreakdown),
    coverage: sessionCoverage,
});
// ── Time-series trends over the store-day rollup (daily_store_metrics) ────────
// The gap the panel had before: no aggregated view of capture analytics over
// days/weeks/months. These read the maintained store-day rollup, so ratios are
// recomputed from summed components at read time (never averaging ratios) and
// bucketing to week/month stays exact.
export const trendBucket = z.enum(["day", "week", "month"]);
export const analyticsTrendQuery = z.object({
    // Omit for all authorized stores; pass an authorized store public id to narrow.
    storeId: z.string().uuid().optional(),
    // Inclusive ISO date bounds (YYYY-MM-DD). Default: last 30 days.
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    bucket: trendBucket.default("day"),
});
/**
 * One point in the series. `date` is the bucket start (YYYY-MM-DD). Volume
 * fields are additive counts; the *Pct / score fields are ratios recomputed
 * from the bucket's summed components and are null when their denominator is
 * zero (no signal ≠ zero).
 */
export const analyticsTrendPoint = z.object({
    date: z.string(),
    captures: z.number().int(),
    analyzedCaptures: z.number().int(),
    observedThrough: z.string().datetime().nullable(),
    computedAt: z.string().datetime().nullable(),
    captureSessions: z.number().int(),
    facings: z.number().int(),
    oosCount: z.number().int(),
    oosRatePct: z.number().nullable(),
    recognitionCoveragePct: z.number().nullable(),
    unknownFacingsPct: z.number().nullable(),
    doubtRatePct: z.number().nullable(),
    frontalVisibilityPct: z.number().nullable(),
    meanConfidencePct: z.number().nullable(),
    qualityScore: z.number().nullable(),
});
export const analyticsTrendResponse = z.object({
    bucket: trendBucket,
    from: z.string(),
    to: z.string(),
    storeId: z.string().uuid().nullable(),
    points: z.array(analyticsTrendPoint),
});
// Rebuild the store-day rollup for a date window (idempotent upsert). Meant to
// be driven by a nightly job; also exposed as an admin endpoint for backfill.
export const analyticsRollupRequest = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export const analyticsRollupResponse = z.object({
    from: z.string(),
    to: z.string(),
    rowsBuilt: z.number().int(),
});
//# sourceMappingURL=analytics.schemas.js.map